import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, GraduationCap, Cpu, Microscope } from 'lucide-react';
import { useTheme } from '../ThemeContext';
import SectionHeader from './SectionHeader';

const stats = [
  { icon: MapPin, label: 'Location', value: 'Boulder, CO' },
  { icon: GraduationCap, label: 'Education', value: 'CU Boulder CS' },
  { icon: Cpu, label: 'Focus', value: 'ML & Data Science' },
  { icon: Microscope, label: 'Research', value: 'LASP & Biofrontiers' },
];

export default function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { isCyberpunk } = useTheme();

  return (
    <section id="about" className={`relative py-24 px-6 transition-colors duration-700 ${
      isCyberpunk ? 'cyber-grid bg-cyber-black' : 'bg-gray-50'
    }`}>
      <div className="max-w-6xl mx-auto">
        <SectionHeader number="01." title="ABOUT" />

        <div ref={ref} className="grid md:grid-cols-5 gap-12">
          {/* Text */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="md:col-span-3 space-y-6"
          >
            <p className={`font-body text-lg leading-relaxed transition-colors duration-500 ${
              isCyberpunk ? 'text-cyber-dim' : 'text-gray-600'
            }`}>
              I'm a Computer Science student at the{' '}
              <span className={isCyberpunk ? 'text-cyber-magenta' : 'text-indigo-600 font-semibold'}>
                University of Colorado Boulder
              </span>{' '}
              with a passion for using technology to solve complex, real-world problems.
              My work spans machine learning, space physics, and
              computational biology.
            </p>
            <p className={`font-body text-lg leading-relaxed transition-colors duration-500 ${
              isCyberpunk ? 'text-cyber-dim' : 'text-gray-600'
            }`}>
              At the{' '}
              <span className={isCyberpunk ? 'text-cyber-magenta' : 'text-indigo-600 font-semibold'}>
                Laboratory for Atmospheric and Space Physics (LASP)
              </span>,
              I develop ML models to analyze Van Allen Probe plasma wave data, achieving
              95% agreement with traditional analysis methods. At{' '}
              <span className={isCyberpunk ? 'text-cyber-magenta' : 'text-indigo-600 font-semibold'}>
                Biofrontiers Institute
              </span>,
              I apply statistical algorithms to analyze firefly synchronization patterns.
            </p>
            <p className={`font-body text-lg leading-relaxed transition-colors duration-500 ${
              isCyberpunk ? 'text-cyber-dim' : 'text-gray-600'
            }`}>
              I was recognized as a{' '}
              <span className={isCyberpunk ? 'text-cyber-pink' : 'text-pink-600 font-semibold'}>
                top analyst among 700+ members
              </span>{' '}
              at NASA's L'Space Mission Concept Academy, where I designed a mock rover
              mission to analyze Lunar lava tubes.
            </p>

          </motion.div>

          {/* Stats cards */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="md:col-span-2 space-y-4"
          >
            {stats.map(({ icon: Icon, label, value }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, x: 30 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
                className={`p-4 flex items-center gap-4 group transition-all duration-500 ${
                  isCyberpunk
                    ? 'neon-border bg-cyber-dark/50'
                    : 'bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md'
                }`}
              >
                <div className={`w-10 h-10 flex items-center justify-center border transition-colors duration-500 ${
                  isCyberpunk
                    ? 'border-cyber-magenta/20 text-cyber-magenta group-hover:bg-cyber-magenta/10'
                    : 'border-indigo-200 text-indigo-500 rounded-lg group-hover:bg-indigo-50'
                }`}>
                  <Icon size={18} />
                </div>
                <div>
                  <p className={`font-mono text-xs uppercase tracking-wider transition-colors duration-500 ${
                    isCyberpunk ? 'text-cyber-dim/60' : 'text-gray-400'
                  }`}>{label}</p>
                  <p className={`font-body text-lg font-medium transition-colors duration-500 ${
                    isCyberpunk ? 'text-white' : 'text-gray-800'
                  }`}>{value}</p>
                </div>
              </motion.div>
            ))}

            {/* GPA */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.9 }}
              className={`p-4 text-center transition-all duration-500 ${
                isCyberpunk
                  ? 'neon-border bg-cyber-dark/50'
                  : 'bg-white border border-gray-200 rounded-lg shadow-sm'
              }`}
            >
              <p className={`font-mono text-xs uppercase tracking-wider mb-1 transition-colors duration-500 ${
                isCyberpunk ? 'text-cyber-dim/60' : 'text-gray-400'
              }`}>GPA</p>
              <p className={`font-display text-3xl font-bold transition-colors duration-500 ${
                isCyberpunk ? 'text-cyber-magenta neon-text-subtle' : 'text-indigo-600'
              }`}>3.7</p>
              <p className={`font-mono text-xs mt-1 transition-colors duration-500 ${
                isCyberpunk ? 'text-cyber-dim/40' : 'text-gray-400'
              }`}>Expected May 2026</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Section divider */}
      {isCyberpunk ? (
        <div className="cyber-divider mt-24 max-w-4xl mx-auto" />
      ) : (
        <div className="mt-24 max-w-4xl mx-auto h-[1px] bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
      )}
    </section>
  );
}
